import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, Check, ExternalLink, FileText, Loader2, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/StatusBadge';
import { StagePicker } from '@/components/StagePicker';
import { StatusPicker } from '@/components/StatusPicker';
import { CompanyMark } from '@/components/CompanyMark';
import { UserAvatar } from '@/components/UserAvatar';
import { OpportunityAssistant } from '@/components/SalesAssistant';
import { useCompanyLookup, useOpportunity, useOppPicklists, useOpportunityNotes, useResourceLookup, useUpdateOpportunity } from '@/api/hooks';
import { cn } from '@/lib/utils';
import { fmtCurrency, fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES } from '@/lib/routes';
import { OrbitLink } from '@/components/insights/OrbitLink';

const AUTOTASK_OPP_URL = 'https://ww31.autotask.net/Mvc/CRM/OpportunityDetail.mvc?opportunityId=';

function probKind(p: number): 'success' | 'warning' | 'destructive' {
  if (p >= 75) return 'success';
  if (p >= 40) return 'warning';
  return 'destructive';
}

function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  try { return format(new Date(d), 'PP'); }
  catch { return '—'; }
}

export default function OpportunityDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number.parseInt(params.id ?? '', 10);

  const detail    = useOpportunity(Number.isFinite(id) ? id : undefined);
  const picklists = useOppPicklists();
  const updateOpp = useUpdateOpportunity();
  const notes     = useOpportunityNotes(Number.isFinite(id) ? id : undefined);

  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft]     = useState('');

  const opp = detail.data?.item;

  const companies   = useCompanyLookup(opp?.companyID != null ? [opp.companyID] : []);
  const owners      = useResourceLookup(opp?.ownerResourceID != null ? [opp.ownerResourceID] : []);

  if (!Number.isFinite(id)) {
    return <div className="container py-8 text-sm text-destructive">Invalid opportunity id.</div>;
  }

  if (detail.isLoading) {
    return (
      <div className="container flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading opportunity #{id}…
      </div>
    );
  }

  if (detail.error) {
    return (
      <div className="container py-8 text-sm text-destructive">
        Failed to load opportunity: {detail.error instanceof Error ? detail.error.message : 'Unknown error'}
      </div>
    );
  }

  if (!opp) return <div className="container py-8 text-sm">Opportunity not found.</div>;

  const stageOpts = picklists.data?.stage ?? [];
  const statusOpts = picklists.data?.status ?? [];

  const companyName = opp.companyID != null
    ? (companies.data?.items.find((c) => c.id === opp.companyID)?.companyName ?? `#${opp.companyID}`)
    : null;

  const ownerResource = opp.ownerResourceID != null
    ? owners.data?.items.find((r) => r.id === opp.ownerResourceID)
    : null;
  const ownerName = ownerResource
    ? `${ownerResource.firstName} ${ownerResource.lastName}`.trim()
    : opp.ownerResourceID != null ? `#${opp.ownerResourceID}` : '—';

  // SOW is always shown (and editable) on its own; other UDFs only when populated.
  const SOW_FIELD = 'Statement of Work';
  const sowValue = opp.userDefinedFields?.find((f) => f.name === SOW_FIELD)?.value ?? null;
  const udfs = (opp.userDefinedFields ?? []).filter(
    (f) => f.name !== SOW_FIELD && f.value != null && f.value.trim() !== '',
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6">
      {/* Breadcrumb / actions */}
      <div className="flex items-center justify-between gap-2">
        <Button asChild variant="ghost" size="sm" className="-ml-2.5 text-muted-foreground hover:text-foreground">
          <Link to={ROUTES.psa.opportunities.all}><ArrowLeft className="h-4 w-4"/> Opportunities</Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <a href={`${AUTOTASK_OPP_URL}${id}`} target="_blank" rel="noreferrer">
            <ExternalLink className="mr-1 h-3.5 w-3.5"/> Open in Autotask
          </a>
        </Button>
      </div>

      {/* Two-column layout */}
      {/* Two-column layout. Desktop placement is explicit (see the side rail below) so the
          rail can grow — e.g. when the assistant is expanded — without pushing Activity down. */}
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_300px] md:items-start">
        {/* ── Main column ── */}
        <div className="space-y-4 md:col-start-1 md:row-start-1">
          {/* Header card */}
          <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <StagePicker
                stageId={opp.stage}
                stageOpts={stageOpts}
                onSelect={(stageId) => updateOpp.mutate({ id, fields: { stage: stageId } })}
                pending={updateOpp.isPending && updateOpp.variables?.fields?.stage !== undefined}
              />
              <StatusPicker
                statusId={opp.status ?? null}
                statusOpts={statusOpts}
                onSelect={(status) => updateOpp.mutate({ id, fields: { status } })}
                pending={updateOpp.isPending && updateOpp.variables?.fields?.status !== undefined}
              />
              {opp.probability != null && (
                <StatusBadge kind={probKind(opp.probability)}>{opp.probability}%</StatusBadge>
              )}
            </div>

            <h1 className="mt-2 text-[22px] font-[650] leading-tight tracking-[-0.025em] text-foreground">
              {opp.title}
            </h1>

            {companyName && (
              <div className="mt-3 flex items-center gap-2.5">
                <CompanyMark name={companyName} size={22}/>
                <OrbitLink companyId={opp.companyID} className="text-[13.5px] font-medium text-foreground">{companyName}</OrbitLink>
                {opp.createDate && (
                  <>
                    <span className="text-muted-foreground/40">·</span>
                    <span className="text-xs text-muted-foreground">
                      Created {format(new Date(opp.createDate), 'PP')}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Description — inline editable */}
            <div className="mt-4 border-t border-divider pt-4">
              {editingDesc ? (
                <div className="space-y-2">
                  <textarea
                    autoFocus
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                    rows={6}
                    value={descDraft}
                    onChange={(e) => setDescDraft(e.target.value)}
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="h-7 gap-1.5"
                      disabled={updateOpp.isPending}
                      onClick={() => {
                        updateOpp.mutate(
                          { id, fields: { description: descDraft } },
                          { onSuccess: () => setEditingDesc(false) },
                        );
                      }}
                    >
                      {updateOpp.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 gap-1.5" onClick={() => setEditingDesc(false)}>
                      <X className="h-3 w-3" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="group relative cursor-pointer rounded-md p-1 -m-1 hover:bg-muted/40 transition-colors"
                  onClick={() => { setDescDraft(opp.description ?? ''); setEditingDesc(true); }}
                  title="Click to edit description"
                >
                  {opp.description ? (
                    <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground/80">
                      {opp.description}
                    </div>
                  ) : (
                    <p className="text-sm italic text-muted-foreground">No description — click to add one.</p>
                  )}
                  <Pencil className="absolute right-1 top-1 h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity" />
                </div>
              )}
            </div>
          </section>

          {/* Narrative fields */}
          {(opp.nextStep || opp.barriers || opp.helpNeeded) && (
            <section className="rounded-lg border border-border bg-card p-5 shadow-sm space-y-4">
              {opp.nextStep && (
                <NarrativeField label="Next Step">{opp.nextStep}</NarrativeField>
              )}
              {opp.barriers && (
                <NarrativeField label="Barriers">{opp.barriers}</NarrativeField>
              )}
              {opp.helpNeeded && (
                <NarrativeField label="Help Needed">{opp.helpNeeded}</NarrativeField>
              )}
            </section>
          )}

          {/* UDFs — Statement of Work is always shown and editable; other UDFs when populated */}
          <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Additional Fields
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              {udfs.map((f) => {
                const val = f.value!.trim();
                return (
                  <div key={f.name}>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{f.name}</div>
                    <div className="mt-0.5"><span>{val}</span></div>
                  </div>
                );
              })}
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{SOW_FIELD}</div>
                <div className="mt-0.5">
                  <SowField
                    value={sowValue}
                    pending={updateOpp.isPending && Array.isArray(updateOpp.variables?.fields?.userDefinedFields)}
                    onSave={(value) =>
                      updateOpp.mutate({ id, fields: { userDefinedFields: [{ name: SOW_FIELD, value }] } })
                    }
                  />
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* ── Side rail ──
            Spans both rows on desktop: its height is then shared across the rows instead of
            inflating row 1, so expanding the Sales Assistant doesn't shunt Activity downward. */}
        <aside className="space-y-4 md:col-start-2 md:row-start-1 md:row-span-2">
          <SideCard title="Details">
            {opp.amount != null && (
              <PropRow label="Amount">
                <span className="tabular-nums">{fmtCurrency(opp.amount)}</span>
              </PropRow>
            )}
            {opp.probability != null && (
              <PropRow label="Probability">
                <StatusBadge kind={probKind(opp.probability)}>{opp.probability}%</StatusBadge>
              </PropRow>
            )}
            <PropRow label="Close Date">{fmtDate(opp.projectedCloseDate)}</PropRow>
            <PropRow label="Created">{fmtDate(opp.createDate)}</PropRow>
            <PropRow label="Owner">
              <span className="inline-flex items-center gap-1.5">
                {ownerResource && <UserAvatar name={ownerName} size={18}/>}
                <span>{ownerName}</span>
              </span>
            </PropRow>
            {companyName && (
              <PropRow label="Company">
                <span className="inline-flex items-center gap-1.5">
                  <CompanyMark name={companyName} size={16}/>
                  <OrbitLink companyId={opp.companyID}>{companyName}</OrbitLink>
                </span>
              </PropRow>
            )}
            {opp.lastActivity && (
              <PropRow label="Last Activity">{fmtRelative(opp.lastActivity)}</PropRow>
            )}
          </SideCard>

          <OpportunityAssistant opportunityId={id} />
        </aside>

        {/* Activity timeline — third grid child so it appears after Details on mobile;
            on desktop it's pinned to col 1 row 2, directly under the main column */}
        {(notes.data?.items.length ?? 0) > 0 && (
          <section className="rounded-lg border border-border bg-card p-5 shadow-sm md:col-start-1 md:row-start-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Activity
            </div>
            <ol className="relative border-l border-divider space-y-5 ml-1">
              {notes.data!.items.map((n) => (
                <li key={n.id} className="ml-4">
                  <span className="absolute -left-[5px] mt-1 h-2.5 w-2.5 rounded-full border-2 border-background bg-muted-foreground/40" />
                  <time className="text-[11px] text-muted-foreground">
                    {format(new Date(n.createDateTime), 'PP · p')}
                  </time>
                  {n.name && (
                    <div className="mt-0.5 text-[12px] font-semibold text-foreground">{n.name}</div>
                  )}
                  {n.note && (
                    <div className="mt-1 whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-foreground/75">
                      {n.note.trim()}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}

function SideCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
      <header className="border-b border-divider bg-muted/40 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </header>
      <div>{children}</div>
    </section>
  );
}

function PropRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-divider px-4 py-2.5 first:border-t-0">
      <span className="shrink-0 pt-[2px] text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className={cn('text-right text-[13px] text-foreground')}>
        {children}
      </div>
    </div>
  );
}

function SowField({
  value,
  onSave,
  pending,
}: {
  value: string | null;
  onSave: (value: string) => void;
  pending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const trimmed = (value ?? '').trim();
  const isUrl = /^https?:\/\//i.test(trimmed);

  const save = () => { onSave(draft.trim()); setEditing(false); };

  if (editing) {
    return (
      <div className="space-y-2">
        <input
          autoFocus
          type="url"
          placeholder="https://… (paste SOW link)"
          className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            if (e.key === 'Escape') setEditing(false);
          }}
        />
        <div className="flex items-center gap-2">
          <Button size="sm" className="h-7 gap-1.5" disabled={pending} onClick={save}>
            {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
          </Button>
          <Button size="sm" variant="ghost" className="h-7 gap-1.5" onClick={() => setEditing(false)}>
            <X className="h-3 w-3" /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-1.5">
      {trimmed ? (
        isUrl ? (
          <a
            href={trimmed}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-brand-strong hover:underline dark:text-brand"
          >
            <FileText className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Open document</span>
          </a>
        ) : (
          <span className="truncate">{trimmed}</span>
        )
      ) : (
        <span className="italic text-muted-foreground">Not set</span>
      )}
      <button
        type="button"
        onClick={() => { setDraft(trimmed); setEditing(true); }}
        className="opacity-0 transition-opacity group-hover:opacity-60 hover:!opacity-100"
        title="Edit Statement of Work"
      >
        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}

function NarrativeField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">{children}</div>
    </div>
  );
}
